import React, { CSSProperties, DragEvent, MouseEvent, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { Token } from '../../reducers/map/mapReducer';
import { shallowEqual, useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import MapElementShaper from '../Map/MapElementShaper';
import EditTokenModal from './EditTokenModal';
import Client from '../../utils/network';

interface TokenRenderProps {
    token: Token,
    onDragStart?: (e:DragEvent<HTMLImageElement>) => void,
    onClick?: (e: MouseEvent) => void,
    updateToken?: (t: Token) => void,
    className?: string,
    style?: CSSProperties,
    alt?: string,
    showHidden?: boolean
}

const TokenRender = ({
    token,
    onDragStart,
    onClick,
    style,
    className,
    alt,
    updateToken,
    showHidden,
}: TokenRenderProps) => {
    const id = token._id?.toString();

    // Decode the access_token to extract the current user's ID
    const userId = useMemo(() => {
        const accessToken = Cookies.get('access_token');
        if (accessToken) {
            try {
                const decodedToken: { userId: string } = (jwtDecode as (val: string) => unknown)(
                    accessToken,
                ) as { userId: string };
                return decodedToken.userId;
                // currentUserId = decodedToken.sub; // Assuming `sub` contains the user ID
            } catch {
                // do nothing
                return undefined;
            }
        }
    }, []);

    const userCanMoveToken = false; token.assignedUsers?.includes(userId ?? '');

    const {
        currentLayer,
        selectedElement,
    } = useSelector((state: Rootstate) => ({
        currentLayer: state.map.mainLayer,
        selectedElement: state.map.selectedElement,
    }), shallowEqual);

    const [showModal, setShowModal] = useState(false);
    const [editableToken, setEditableToken] = useState<Token>(cloneDeep(token));

    const openTokenModal = () => {
        setEditableToken(cloneDeep(token));
        setShowModal(true);
    };

    const isSelectedElement = selectedElement?._id.toString() === id && selectedElement.type === 'token';
    const tokenMatchesLayer = currentLayer === 'dm' && token.dm || currentLayer === 'public' && !token.dm;
    return tokenMatchesLayer || showHidden ? (
        <div
            className={className}
            style={{
                ...style,
                opacity: (tokenMatchesLayer || isSelectedElement) ? '1' : '0.5',
                border: token.borderColor ? `2px solid ${token.borderColor}` : undefined,
                borderRadius: "100%",
            }}
            draggable={tokenMatchesLayer || userCanMoveToken} // Allow dragging if the user is assigned
            onDragStart={(tokenMatchesLayer || userCanMoveToken) ? onDragStart : undefined}
            onClick={(tokenMatchesLayer || userCanMoveToken) ? onClick : undefined}
        >
            <img
                alt={alt ?? 'token'}
                src={`${Client.getBaseUrl()}/api/image/${token.image}`}
            />
            {isSelectedElement && (
                <MapElementShaper
                    dmLayer={token.dm}
                    onLayerChange={(x) => {
                        const newToken = cloneDeep(token);
                        newToken.dm = x;
                        updateToken?.(newToken);
                    }}
                    onUiToggle={openTokenModal}
                />
            )}
            <EditTokenModal
                show={showModal}
                token={editableToken}
                toggle={() => setShowModal(!showModal)}
                onChange={(token) => setEditableToken(token)}
                onCancel={() => setShowModal(!showModal)}
                onSubmit={() => {
                    setShowModal(false);
                    updateToken?.(editableToken);
                }}
            />
        </div>
    ) : null;
};

export default TokenRender;
